import { useState, useEffect, useMemo, useRef } from "react";
import Sidebar, { dispatchSidebarUpdate } from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useAuth } from "../Auth-Context/AuthContext";
import { useToast } from "../Auth-Context/ToastContext";
import {
  listInteractions,
  logInteraction,
  deleteInteraction,
  listContacts,
  getContactPersons,
  sendEmailInteraction,
  logInteractionWithRecording,
} from "../api/services";
import { RecordingPlayer, AttachmentLink, InteractionNotes } from "../components/InteractionMedia";
import EmailRecipientsField, {
  joinEmails,
  validateRecipientLists,
} from "../components/EmailRecipientsField";
import {
  Phone, Mail, Calendar, FileText, TrendingUp, ArrowUp,
  ChevronLeft, ChevronRight, Loader, Search, X, CheckCircle,
  Trash2, AlertTriangle, Mic, Square, Paperclip, Send,
  Bold, Italic, Underline, List, ListOrdered, Edit2, Plus
} from "lucide-react";

const TYPE_ICON = { EMAIL: Mail, CALL: Phone, MEETING: Calendar, NOTE: FileText };
const TYPE_STYLE = {
  EMAIL:   { bg: "#e0f2fe", color: "#0284c7" },
  CALL:    { bg: "#dcfce7", color: "#16a34a" },
  MEETING: { bg: "#ede9fe", color: "#7c3aed" },
  NOTE:    { bg: "#fef3c7", color: "#d97706" },
};

const LOG_TYPES = ["CALL", "EMAIL", "MEETING", "NOTE"];
const LOGGABLE_ROLES = ["SALES_REP", "MANAGER", "ADMIN", "SUPER_ADMIN"];
const BCC_ALLOWED_ROLES = ["SALES_REP", "MANAGER", "ADMIN", "SUPER_ADMIN"];
const PAGE_SIZE = 10;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const stripHtmlToText = (html = "") => {
  if (!html) return "";

  if (typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || "").replace(/\s+/g, " ").trim();
  }

  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const NOTE_CATEGORY_RESEARCH     = "RESEARCH MAPPING";
const NOTE_CATEGORY_FOLLOWUP     = "FOLLOW-UP";
const NOTE_CATEGORY_OFFICE_VISIT = "OFFICE VISIT";

const NOTE_CATEGORY_LABELS = {
  RESEARCH:     NOTE_CATEGORY_RESEARCH,
  FOLLOWUP:     NOTE_CATEGORY_FOLLOWUP,
  OFFICE_VISIT: NOTE_CATEGORY_OFFICE_VISIT,
};

const NOTE_CATEGORY_OPTIONS = [
  { key: "RESEARCH",     label: "Research Mapping" },
  { key: "FOLLOWUP",     label: "Follow-up" },
  { key: "OFFICE_VISIT", label: "Office Visit" },
];

function NoteCategoryCheckboxes({ value, onChange }) {
  return (
    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
      {NOTE_CATEGORY_OPTIONS.map((opt) => {
        const checked = value === opt.key;
        return (
          <label
            key={opt.key}
            className="inline-flex items-center gap-2 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onChange(checked ? null : opt.key)}
              className="w-4 h-4 rounded border-neutral-300 accent-[#009DB9] cursor-pointer"
            />
            <span
              className="text-[12px] font-semibold"
              style={{ color: checked ? "#009DB9" : "#525252" }}
            >
              {opt.label}
            </span>
          </label>
        );
      })}
    </div>
  );
}

const formatElapsed = (seconds = 0) => {
  if (!Number.isFinite(seconds)) return "0:00";

  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
};

function AudioRecorder({ onRecordingComplete, onClear, onError, disabled }) {
  const [status, setStatus] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      stopStream();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      onError?.("Audio recording isn't supported in this browser.");
      return;
    }

    setStatus("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const supportedType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find(
        (t) => MediaRecorder.isTypeSupported?.(t)
      );
      const recorder = supportedType ? new MediaRecorder(stream, { mimeType: supportedType }) : new MediaRecorder(stream);

      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = () => {
        onError?.("Recording failed unexpectedly. Please try again.");
        clearInterval(timerRef.current);
        stopStream();
        setStatus("idle");
      };
      recorder.onstop = () => {
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: supportedType || "audio/webm" });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setStatus("stopped");
        stopStream();
        onRecordingComplete?.(blob, url, elapsedRef.current);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();

      elapsedRef.current = 0;
      setElapsed(0);
      setStatus("recording");
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
      }, 1000);
    } catch (err) {
      console.error("Microphone access failed:", err);
      setStatus("idle");
      if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") {
        onError?.("Microphone access was denied. Please allow microphone access to record the call.");
      } else if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
        onError?.("No microphone was found on this device.");
      } else {
        onError?.("Couldn't start recording. Please try again.");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && status === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const clearRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setStatus("idle");
    setElapsed(0);
    onClear?.();
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Audio Recording</label>

      {status === "idle" && (
        <button
          type="button"
          onClick={startRecording}
          disabled={disabled}
          className="flex items-center justify-center gap-2 h-8 rounded-lg border border-neutral-200 bg-white text-[12px] font-semibold text-neutral-700 hover:border-[#009DB9] hover:text-[#009DB9] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Mic size={13} /> Start Recording
        </button>
      )}

      {status === "requesting" && (
        <div className="flex items-center justify-center gap-2 h-8 text-[12px] text-neutral-500 border border-neutral-200 rounded-lg">
          <Loader size={13} className="animate-spin" /> Requesting microphone access…
        </div>
      )}

      {status === "recording" && (
        <div className="flex items-center justify-between h-8 gap-2 px-3 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-center gap-2">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full bg-red-400 rounded-full opacity-75 animate-ping" />
              <span className="relative inline-flex w-2 h-2 bg-red-500 rounded-full" />
            </span>
            <span className="text-[12px] font-semibold text-red-600">
              Recording… {formatElapsed(elapsed)}
            </span>
          </div>
          <button
            type="button"
            onClick={stopRecording}
            className="flex items-center gap-1.5 px-2.5 h-6 rounded-lg text-[11px] font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            <Square size={9} fill="white" /> Stop
          </button>
        </div>
      )}

      {status === "stopped" && audioUrl && (
        <div className="flex flex-col gap-2 p-2.5 border border-neutral-200 rounded-lg bg-neutral-50">
          <audio controls src={audioUrl} className="w-full h-8" />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-neutral-400">Duration: {formatElapsed(elapsed)}</span>
            <button
              type="button"
              onClick={clearRecording}
              disabled={disabled}
              className="text-[11px] font-semibold text-red-500 hover:underline disabled:opacity-40"
            >
              Remove &amp; re-record
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailBodyEditorModal({ initialHtml, onCancel, onSave }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialHtml || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
  };

  const handleSave = () => {
    onSave(editorRef.current?.innerHTML || "");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-neutral-100">
          <h3 className="text-[15px] font-bold text-neutral-900">Email Body</h3>
          <button onClick={onCancel} className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100">
            <X size={15} />
          </button>
        </div>

        <div className="flex items-center flex-shrink-0 gap-1 px-4 py-2 border-b border-neutral-100">
          <button type="button" onClick={() => exec("bold")} title="Bold"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
            <Bold size={14} />
          </button>
          <button type="button" onClick={() => exec("italic")} title="Italic"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
            <Italic size={14} />
          </button>
          <button type="button" onClick={() => exec("underline")} title="Underline"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
            <Underline size={14} />
          </button>
          <span className="w-px h-5 mx-1 bg-neutral-200" />
          <button type="button" onClick={() => exec("insertUnorderedList")} title="Bullet list"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
            <List size={14} />
          </button>
          <button type="button" onClick={() => exec("insertOrderedList")} title="Numbered list"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
            <ListOrdered size={14} />
          </button>
        </div>

        <div className="flex-1 px-6 py-4 overflow-y-auto">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="min-h-[220px] text-[13px] text-neutral-800 outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
          />
        </div>

        <div className="flex items-center justify-end flex-shrink-0 gap-3 px-6 py-4 border-t border-neutral-100">
          <button onClick={onCancel} className="px-4 py-2 text-[13px] font-medium rounded-xl text-neutral-600 hover:bg-neutral-100">
            Cancel
          </button>
          <button onClick={handleSave} className="px-5 py-2 text-[13px] font-semibold text-white rounded-xl" style={{ background: "#009DB9" }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ interaction, onConfirm, onCancel, loading }) {
  if (!interaction) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div
        className="relative w-full max-w-sm overflow-hidden bg-white rounded-2xl"
        style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}
      >
        <div className="h-1 bg-red-500 rounded-t-2xl" />
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-red-50">
            <AlertTriangle size={22} className="text-red-500" />
          </div>
          <h3 className="text-[16px] font-bold text-neutral-900 mb-1">Delete Interaction</h3>
          <p className="text-[12px] text-neutral-500 mb-1">
            Are you sure you want to delete this interaction?
          </p>
          <div className="px-3 py-2 mb-5 bg-neutral-50 rounded-xl">
            <div className="text-[12px] font-semibold text-neutral-700">{interaction.subject}</div>
            <div className="text-[11px] text-neutral-400 mt-0.5">{interaction.contactName} · {interaction.type}</div>
          </div>
          <p className="text-[11px] text-red-500 mb-5">⚠ This action cannot be undone.</p>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 h-10 rounded-xl text-[13px] font-semibold text-neutral-600 border border-neutral-200 hover:bg-neutral-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 h-10 rounded-xl text-[13px] font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {loading ? "Deleting…" : "Yes, Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InteractionCard({ interaction, canDelete, onDeleteClick }) {
  const displayType =
    interaction.type === "CALL"    ? "Call"    :
    interaction.type === "EMAIL"   ? "Email"   :
    interaction.type === "MEETING" ? "Meeting" : "Note";

  const Icon      = TYPE_ICON[interaction.type] || FileText;
  const s         = TYPE_STYLE[interaction.type] || TYPE_STYLE.NOTE;
  const occurredAt = new Date(interaction.occurredAt);
  const initials  = interaction.contactName?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "NA";

  return (
    <div className="p-4 transition-shadow bg-white border rounded-xl border-neutral-100 hover:shadow-md">
      <div className="flex items-start justify-between mb-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1"
          style={{ background: s.bg, color: s.color }}>
          <Icon size={11} strokeWidth={2} />{displayType}
        </span>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-[12px] text-neutral-700 font-medium">{occurredAt.toLocaleDateString()}</div>
            <div className="text-[10px] text-neutral-400">{occurredAt.toLocaleTimeString()}</div>
          </div>
          {canDelete && (
            <button
              onClick={() => onDeleteClick(interaction)}
              className="flex items-center justify-center text-red-400 transition-colors rounded-lg w-7 h-7 hover:bg-red-50 hover:text-red-500"
              title="Delete interaction"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
          style={{ background: "#009DB9" }}>
          {initials}
        </div>
        <div>
          <div className="text-[13px] font-semibold text-neutral-800">{interaction.contactName}</div>
          {interaction.durationMinutes && (
            <div className="text-[10px] text-neutral-400">{interaction.durationMinutes} min</div>
          )}
        </div>
      </div>

      <div className="mt-2">
        <div className="text-[12px] font-medium text-neutral-800">{interaction.subject}</div>
        {interaction.notes && (
          <InteractionNotes notes={interaction.notes} className="text-[11px] text-neutral-500 mt-1 line-clamp-2" />
        )}
      </div>

      {interaction.type === "EMAIL" && (interaction.primaryEmail || interaction.secondaryEmail || interaction.bccEmail) && (
        <div className="mt-1 space-y-0.5">
          {interaction.primaryEmail && (
            <div className="text-[10px] text-neutral-500"><span className="font-medium">To:</span> {interaction.primaryEmail}</div>
          )}
          {interaction.secondaryEmail && (
            <div className="text-[10px] text-neutral-500"><span className="font-medium">CC:</span> {interaction.secondaryEmail}</div>
          )}
          {interaction.bccEmail && (
            <div className="text-[10px] text-neutral-500"><span className="font-medium">BCC:</span> {interaction.bccEmail}</div>
          )}
        </div>
      )}

      {interaction.type === "CALL" && <RecordingPlayer interaction={interaction} />}
      {interaction.type === "EMAIL" && <AttachmentLink interaction={interaction} />}
    </div>
  );
}

export default function Interactions() {
  const { user } = useAuth();
  const toast = useToast();

  const canLogInteractions = LOGGABLE_ROLES.includes(user?.role);
  const canDelete = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN";
  const canUseBcc = BCC_ALLOWED_ROLES.includes(user?.role);

  const [interactions, setInteractions] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [filter,       setFilter]       = useState("ALL");
  const [sort,         setSort]         = useState("Newest First");
  const [page,         setPage]         = useState(1);
  const [isMobile,     setIsMobile]     = useState(false);

  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [logType,      setLogType]      = useState("CALL");
  const [logContactId, setLogContactId] = useState("");

  const [logSubject,   setLogSubject]   = useState("");

  const [noteCategory, setNoteCategory] = useState(null);
  const [noteDetail,   setNoteDetail]   = useState("");

  const [logNotes,     setLogNotes]     = useState("");
  const [logDuration,  setLogDuration]  = useState("");
  const [formLoading,  setFormLoading]  = useState(false);

  const [primaryPhone,   setPrimaryPhone]   = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");

  const [callMode, setCallMode] = useState("notes");
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioDurationSeconds, setAudioDurationSeconds] = useState(0);

  const [primaryEmail,   setPrimaryEmail]   = useState([]);
  const [ccEmails,       setCcEmails]       = useState([]);
  const [bccEmails,      setBccEmails]      = useState([]);
  const [attachment,       setAttachment]      = useState(null);
  const [attachmentError,  setAttachmentError] = useState("");
  const [emailBodyHtml, setEmailBodyHtml] = useState("");
  const [showEmailBodyModal, setShowEmailBodyModal] = useState(false);

  const [meetingLink,    setMeetingLink]    = useState("");

  const [formKey, setFormKey] = useState(0);

  const [searchCompany,        setSearchCompany]        = useState("");
  const [showCompanyDropdown,  setShowCompanyDropdown]  = useState(false);
  const [selectedContact,      setSelectedContact]      = useState(null);

  const [companies, setCompanies] = useState([]);

  const [persons, setPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (user) {
      loadInteractions();
      loadCompanies();
    }
  }, [user]);

  const loadInteractions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listInteractions({ page: 0, size: 100 });
      setInteractions(data.content || []);
    } catch (err) {
      console.error("Failed to load interactions:", err);
      setError(err.response?.data?.message || "Failed to load interactions");
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await listContacts({ page: 0, size: 100 });
      setCompanies(data.content || []);
    } catch (err) {
      console.error("Failed to load companies:", err);
    }
  };

  const fetchPersonsForContact = async (contactId) => {
    if (!contactId) {
      setPersons([]);
      return;
    }
    setLoadingPersons(true);
    try {
      const data = await getContactPersons({ contactId });
      setPersons(data || []);
    } catch (err) {
      console.error("Failed to fetch contact persons:", err);
      setPersons([]);
    } finally {
      setLoadingPersons(false);
    }
  };

  const emailRecipientOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    if (selectedContact?.email) {
      const name = `${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim()
        || selectedContact.company || "Company contact";
      options.push({ value: selectedContact.email, label: `${name} — ${selectedContact.email}` });
      seen.add(selectedContact.email);
    }

    (persons || []).forEach((p) => {
      const email = p.email;
      if (email && !seen.has(email)) {
        const name = p.fullName || `${p.firstName || ""} ${p.lastName || ""}`.trim() || "Contact person";
        options.push({ value: email, label: `${name}${p.primaryContact ? " (Primary)" : ""} — ${email}` });
        seen.add(email);
      }
    });

    return options;
  }, [selectedContact, persons]);

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf") {
      setAttachmentError("Only PDF files are supported.");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachmentError("PDF must be smaller than 10MB.");
      return;
    }
    setAttachmentError("");
    setAttachment(file);
  };

  const resetForm = () => {
    setLogType("CALL");
    setLogContactId("");
    setLogSubject("");
    setNoteCategory(null);
    setNoteDetail("");
    setLogNotes("");
    setLogDuration("");
    setPrimaryPhone("");
    setSecondaryPhone("");
    setCallMode("notes");
    setPrimaryEmail([]);
    setCcEmails([]);
    setBccEmails([]);
    setAttachment(null);
    setAttachmentError("");
    setEmailBodyHtml("");
    setShowEmailBodyModal(false);
    setMeetingLink("");
    setSearchCompany("");
    setSelectedContact(null);
    setShowCompanyDropdown(false);
    setPersons([]);
    setAudioBlob(null);
    setAudioDurationSeconds(0);
    setFormKey((k) => k + 1);
  };

  const buildSubject = () => {
    if (logType === "NOTE") {
      if (!noteCategory) return "";
      const category = NOTE_CATEGORY_LABELS[noteCategory];
      return noteDetail.trim() ? `${category} - ${noteDetail.trim()}` : category;
    }
    return logSubject.trim();
  };

  const isSubjectValid = logType === "NOTE" ? !!noteCategory : !!logSubject.trim();

  const isEmailReady =
    logType !== "EMAIL" ||
    (primaryEmail.length > 0 && !!stripHtmlToText(emailBodyHtml));

  const showGenericNotesField =
    logType === "NOTE" ||
    logType === "MEETING" ||
    (logType === "CALL" && callMode === "notes");

  const handleSave = async () => {
    if (!logContactId) {
      toast.error("Please select a company before logging an interaction.");
      return;
    }
    if (!isSubjectValid) {
      alert(logType === "NOTE" ? "Please select a note category" : "Please enter a subject");
      return;
    }
    if (logType === "EMAIL" && primaryEmail.length === 0) {
      toast.error("Please select at least one recipient before sending the email.");
      return;
    }
    if (logType === "EMAIL" && !stripHtmlToText(emailBodyHtml)) {
      toast.error("Please write the email body before sending it.");
      return;
    }
    if (logType === "EMAIL") {
      const check = validateRecipientLists({
        to: primaryEmail,
        cc: ccEmails,
        bcc: canUseBcc ? bccEmails : [],
      });
      if (!check.ok) {
        toast.error(check.message);
        return;
      }
    }
    if (logType === "CALL" && callMode === "audio" && !audioBlob) {
      toast.error("Please record the call first, or switch to Notes.");
      return;
    }

    setFormLoading(true);
    try {
      const subject = buildSubject();
      const occurredAt = new Date().toISOString();
      let newInteraction;

      if (logType === "EMAIL") {
        newInteraction = await sendEmailInteraction({
          contactId: logContactId,
          to: joinEmails(primaryEmail),
          cc: joinEmails(ccEmails),
          bcc: canUseBcc ? joinEmails(bccEmails) : undefined,
          subject,
          body: emailBodyHtml,
          attachment,
        });
      } else if (logType === "CALL" && callMode === "audio") {
        newInteraction = await logInteractionWithRecording({
          type: "CALL",
          subject,
          contactId: logContactId,
          notes: "",
          durationMinutes: logDuration ? parseInt(logDuration) : null,
          primaryPhone,
          secondaryPhone,
          recording: audioBlob,
          recordingDurationSeconds: audioDurationSeconds,
          occurredAt,
        });
      } else {
        newInteraction = await logInteraction({
          type: logType,
          subject,
          contactId: logContactId,
          notes: logNotes,
          durationMinutes: logDuration ? parseInt(logDuration) : null,
          occurredAt,
          primaryPhone: logType === "CALL" ? primaryPhone : null,
          secondaryPhone: logType === "CALL" ? secondaryPhone : null,
          meetingLink: logType === "MEETING" ? meetingLink : null,
        });
      }

      const updatedInteractions = [newInteraction, ...interactions];
      setInteractions(updatedInteractions);
      dispatchSidebarUpdate({ interactions: updatedInteractions.length });

      toast.success(
        logType === "EMAIL" ? `Email sent to ${joinEmails(primaryEmail)}.` :
        logType === "CALL" ? "Call interaction logged." :
        logType === "MEETING" ? "Meeting logged." :
        "Interaction logged."
      );

      resetForm();
      setPage(1);
    } catch (err) {
      console.error("Failed to save interaction:", err);
      toast.error(err.response?.data?.message || "Failed to save interaction. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (interaction) => {
    setDeleteTarget(interaction);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteInteraction(deleteTarget.id);
      const updatedInteractions = interactions.filter(i => i.id !== deleteTarget.id);
      setInteractions(updatedInteractions);
      dispatchSidebarUpdate({ interactions: updatedInteractions.length });
      toast.success("Interaction deleted.");
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete interaction:", err);
      toast.error(err.response?.data?.message || "Failed to delete interaction. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleteLoading) setDeleteTarget(null);
  };

  const filteredCompanies = companies.filter(c =>
    c.company?.toLowerCase().includes(searchCompany.toLowerCase()) ||
    `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchCompany.toLowerCase())
  );

  const totalCount    = interactions.length;
  const callsCount    = interactions.filter(i => i.type === "CALL").length;
  const emailsCount   = interactions.filter(i => i.type === "EMAIL").length;
  const meetingsCount = interactions.filter(i => i.type === "MEETING").length;
  const notesCount    = interactions.filter(i => i.type === "NOTE").length;
  const counts = { ALL: totalCount, CALL: callsCount, EMAIL: emailsCount, MEETING: meetingsCount, NOTE: notesCount };

  const filtered   = interactions.filter(i => filter === "ALL" || i.type === filter);
  const sorted     = sort === "Newest First" ? [...filtered] : [...filtered].reverse();
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged      = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const FILTERS = ["ALL", "CALL", "EMAIL", "MEETING", "NOTE"];
  const getFilterDisplayName = (f) =>
    f === "ALL" ? "All" : f === "CALL" ? "Calls" : f === "EMAIL" ? "Emails" : f === "MEETING" ? "Meetings" : "Notes";

  const statCards = [
    { label: "Total Interactions", icon: TrendingUp, value: totalCount,    trendColor: "#009DB9" },
    { label: "Calls",              icon: Phone,      value: callsCount,    trendColor: "#16a34a" },
    { label: "Emails",             icon: Mail,       value: emailsCount,   trendColor: "#0284c7" },
    { label: "Meetings",           icon: Calendar,   value: meetingsCount, trendColor: "#009DB9" },
  ];

  const tableColumns = canDelete
    ? ["TYPE", "COMPANY", "SUBJECT", "DURATION", "DATE & TIME", "ACTIONS"]
    : ["TYPE", "COMPANY", "SUBJECT", "DURATION", "DATE & TIME"];

  const gridTemplate = canDelete
    ? "28px 80px 140px 1fr 80px 130px 70px"
    : "28px 80px 140px 1fr 80px 130px";

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-50">
      <Sidebar />

      <DeleteModal
        interaction={deleteTarget}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteLoading}
      />

      {showEmailBodyModal && (
        <EmailBodyEditorModal
          initialHtml={emailBodyHtml}
          onCancel={() => setShowEmailBodyModal(false)}
          onSave={(html) => {
            setEmailBodyHtml(html);
            setShowEmailBodyModal(false);
          }}
        />
      )}

      <div className="flex flex-col flex-1 overflow-hidden">
        <Topbar title="Interactions" subtitle="Log and manage interactions" />

        <main className="flex-1 px-4 py-4 overflow-y-auto sm:py-5 sm:px-7">

          <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4 sm:gap-3.5 sm:mb-5">
            {statCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <div key={i} className="p-3 bg-white border sm:p-4 rounded-xl border-neutral-100"
                  style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                  <div className="text-[9px] sm:text-[10px] font-semibold text-neutral-400 uppercase tracking-widest mb-1.5 sm:mb-2 flex items-center gap-1">
                    <Icon size={10} className="sm:w-3 sm:h-3 text-neutral-400" />
                    <span className="text-[11px] sm:text-[10px]">{card.label}</span>
                  </div>
                  <div className="text-xl sm:text-[28px] font-bold text-neutral-900 tracking-tight leading-none mb-1.5 sm:mb-2">
                    {card.value}
                  </div>
                  <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold" style={{ color: card.trendColor }}>
                    <ArrowUp size={10} strokeWidth={2.5} />{card.value} total
                  </div>
                </div>
              );
            })}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader size={32} className="animate-spin text-[#009DB9]" />
            </div>
          )}

          {error && !loading && (
            <div className="p-4 mb-4 text-sm text-red-600 rounded-lg bg-red-50">{error}</div>
          )}

          {!loading && !error && (
            <div className={`flex flex-col items-stretch gap-4 ${canLogInteractions ? 'lg:flex-row' : ''}`}>
              <div className={`flex flex-col flex-1 overflow-hidden bg-white border rounded-2xl border-neutral-100 ${!canLogInteractions ? 'w-full' : ''}`}
                style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>

                <div className="flex flex-wrap items-center flex-shrink-0 gap-2 px-4 py-3 border-b sm:px-5 border-neutral-100">
                  <div className="flex flex-wrap items-center gap-1">
                    {FILTERS.map((f) => {
                      const Icon    = f === "CALL" ? Phone : f === "EMAIL" ? Mail : f === "MEETING" ? Calendar : f === "NOTE" ? FileText : null;
                      const isActive = filter === f;
                      return (
                        <button key={f} onClick={() => { setFilter(f); setPage(1); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors whitespace-nowrap"
                          style={isActive
                            ? { background: "#009DB9", color: "#fff", borderColor: "#009DB9" }
                            : { background: "#fff", color: "#525252", borderColor: "#e5e5e5" }}>
                          {Icon && <Icon size={12} strokeWidth={2} />}
                          <span>{getFilterDisplayName(f)}</span>
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-0.5"
                            style={isActive ? { background: "rgba(255,255,255,0.25)", color: "#fff" } : { background: "#f0f0f0", color: "#737373" }}>
                            {counts[f]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex-1" />
                  <select value={sort} onChange={(e) => setSort(e.target.value)}
                    className="h-8 border border-neutral-200 rounded-lg px-2 text-[12px] text-neutral-600 outline-none focus:border-[#009DB9] bg-white cursor-pointer">
                    <option>Newest First</option>
                    <option>Oldest First</option>
                  </select>
                </div>

                {!isMobile && (
                  <div className="flex-1 overflow-x-auto">
                    <div className="min-w-[800px]">
                      <div
                        className="grid items-center gap-3 px-4 sm:px-5 py-2.5 border-b border-neutral-100 bg-neutral-50/80 sticky top-0"
                        style={{ gridTemplateColumns: gridTemplate }}
                      >
                        <input type="checkbox" className="w-3.5 h-3.5 rounded accent-[#009DB9]" />
                        {tableColumns.map((h) => (
                          <div key={h} className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">{h}</div>
                        ))}
                      </div>

                      {paged.length === 0 ? (
                        <div className="py-12 text-center text-[13px] text-neutral-400">No interactions found.</div>
                      ) : (
                        paged.map((item) => {
                          const displayType =
                            item.type === "CALL"    ? "Call"    :
                            item.type === "EMAIL"   ? "Email"   :
                            item.type === "MEETING" ? "Meeting" : "Note";
                          const Icon       = TYPE_ICON[item.type] || FileText;
                          const s          = TYPE_STYLE[item.type] || TYPE_STYLE.NOTE;
                          const occurredAt = new Date(item.occurredAt);
                          const initials   = item.contactName?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "NA";

                          return (
                            <div
                              key={item.id}
                              className="grid items-start gap-3 px-4 py-3 transition-colors border-b sm:px-5 border-neutral-100 hover:bg-neutral-50/60"
                              style={{ gridTemplateColumns: gridTemplate }}
                            >
                              <input type="checkbox" className="w-3.5 h-3.5 rounded accent-[#009DB9] mt-0.5" />

                              <div>
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1"
                                  style={{ background: s.bg, color: s.color }}>
                                  <Icon size={11} strokeWidth={2} />{displayType}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                                  style={{ background: "#009DB9" }}>
                                  {initials}
                                </div>
                                <span className="text-[12px] font-semibold text-neutral-800 truncate">{item.contactName}</span>
                              </div>

                              <div>
                                <div className="text-[12px] font-medium text-neutral-800">{item.subject}</div>
                                {item.notes && (
                                  <InteractionNotes notes={item.notes} className="text-[11px] text-neutral-400 mt-0.5 line-clamp-1" />
                                )}
                                {item.type === "EMAIL" && (item.primaryEmail || item.secondaryEmail || item.bccEmail) && (
                                  <div className="mt-1 space-y-0.5">
                                    {item.primaryEmail && (
                                      <div className="text-[10px] text-neutral-500"><span className="font-medium">To:</span> {item.primaryEmail}</div>
                                    )}
                                    {item.secondaryEmail && (
                                      <div className="text-[10px] text-neutral-500"><span className="font-medium">CC:</span> {item.secondaryEmail}</div>
                                    )}
                                    {item.bccEmail && (
                                      <div className="text-[10px] text-neutral-500"><span className="font-medium">BCC:</span> {item.bccEmail}</div>
                                    )}
                                  </div>
                                )}
                                {item.type === "CALL" && <RecordingPlayer interaction={item} />}
                                {item.type === "EMAIL" && <AttachmentLink interaction={item} />}
                              </div>

                              <div className="text-[12px] text-neutral-500">
                                {item.durationMinutes ? `${item.durationMinutes} min` : "—"}
                              </div>

                              <div>
                                <div className="text-[12px] text-neutral-700 font-medium">{occurredAt.toLocaleDateString()}</div>
                                <div className="text-[11px] text-neutral-400">{occurredAt.toLocaleTimeString()}</div>
                              </div>

                              {canDelete && (
                                <div className="flex items-center">
                                  <button
                                    onClick={() => handleDeleteClick(item)}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-red-500 border border-red-200 hover:bg-red-50 transition-colors"
                                    title="Delete interaction"
                                  >
                                    <Trash2 size={12} />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {isMobile && (
                  <div className="p-4 space-y-3">
                    {paged.length === 0 ? (
                      <div className="py-12 text-center text-[13px] text-neutral-400">No interactions found.</div>
                    ) : (
                      paged.map((item) => (
                        <InteractionCard
                          key={item.id}
                          interaction={item}
                          canDelete={canDelete}
                          onDeleteClick={handleDeleteClick}
                        />
                      ))
                    )}
                  </div>
                )}

                {sorted.length > 0 && (
                  <div className="flex flex-col items-center justify-between flex-shrink-0 gap-3 px-4 py-3 border-t sm:flex-row sm:px-5 border-neutral-100">
                    <span className="text-[11px] text-neutral-400 order-2 sm:order-1">
                      Showing {paged.length} of {sorted.length}
                    </span>
                    <div className="flex items-center order-1 gap-1 sm:order-2">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                        className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30">
                        <ChevronLeft size={13} />
                      </button>
                      <div className="flex items-center gap-1 overflow-x-auto max-w-[200px]">
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                          <button key={p} onClick={() => setPage(p)}
                            className="min-w-[28px] w-7 h-7 rounded-lg text-[11px] sm:text-[12px] font-medium transition-colors"
                            style={page === p ? { background: "#009DB9", color: "#fff" } : { color: "#737373" }}>
                            {p}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                        className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100 disabled:opacity-30">
                        <ChevronRight size={13} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {canLogInteractions && (
                <div className="flex flex-col overflow-hidden bg-white border rounded-2xl border-neutral-100 w-full lg:w-[380px] xl:w-[420px]"
                  style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>

                  <div className="flex-shrink-0 px-4 py-4 border-b sm:px-5 border-neutral-100">
                    <span className="text-[14px] font-bold text-neutral-900">Log Interaction</span>
                  </div>

                  <div className="flex flex-col flex-1 gap-4 px-4 py-4 overflow-y-auto sm:px-5">

                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Type</label>
                      <div className="grid grid-cols-2 gap-2">
                        {LOG_TYPES.map((t) => {
                          const Icon    = TYPE_ICON[t];
                          const s       = TYPE_STYLE[t];
                          const isActive = logType === t;
                          const displayName = t === "CALL" ? "Call" : t === "EMAIL" ? "Email" : t === "MEETING" ? "Meeting" : "Note";
                          return (
                            <button key={t} onClick={() => setLogType(t)}
                              className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-[13px] font-medium transition-all"
                              style={isActive ? { background: s.bg, color: s.color, borderColor: s.color + "60" } : { background: "#fff", color: "#525252", borderColor: "#e5e5e5" }}>
                              <Icon size={14} strokeWidth={2} />
                              <span>{displayName}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Company *</label>
                      <div className="relative">
                        <div className="flex items-center gap-2 border border-neutral-200 rounded-lg px-3 focus-within:border-[#009DB9]">
                          <Search size={12} className="text-neutral-400" />
                          <input
                            type="text"
                            value={searchCompany}
                            onChange={(e) => { setSearchCompany(e.target.value); setShowCompanyDropdown(true); }}
                            onFocus={() => setShowCompanyDropdown(true)}
                            placeholder="Search company..."
                            className="flex-1 h-8 bg-transparent text-[12px] outline-none"
                          />
                          {searchCompany && (
                            <button onClick={() => {
                              setSearchCompany("");
                              setLogContactId("");
                              setSelectedContact(null);
                              setPrimaryEmail([]);
                              setCcEmails([]);
                              setBccEmails([]);
                              setPersons([]);
                            }}>
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        {showCompanyDropdown && filteredCompanies.length > 0 && (
                          <div className="absolute left-0 right-0 z-10 mt-1 overflow-y-auto bg-white border rounded-lg shadow-lg top-full max-h-48">
                            {filteredCompanies.map(contact => (
                              <button key={contact.id}
                                onClick={() => {
                                  setLogContactId(contact.id);
                                  setSelectedContact(contact);
                                  setSearchCompany(contact.company || `${contact.firstName} ${contact.lastName}`);
                                  setShowCompanyDropdown(false);
                                  setPrimaryEmail([]);
                                  setCcEmails([]);
                                  setBccEmails([]);
                                  fetchPersonsForContact(contact.id);
                                }}
                                className="w-full text-left px-3 py-2 text-[12px] hover:bg-neutral-50">
                                <div className="font-medium">{contact.company}</div>
                                <div className="text-neutral-400 text-[10px]">{contact.firstName} {contact.lastName}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedContact && (
                        <div className="mt-1 text-[11px] text-green-600 flex items-center gap-1">
                          <CheckCircle size={11} /> Selected: {selectedContact.firstName} {selectedContact.lastName}
                        </div>
                      )}
                    </div>

                    {logType === "NOTE" ? (
                      <div className="flex flex-col gap-2">
                        <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Note Category *</label>
                        <div className="px-1 py-1">
                          <NoteCategoryCheckboxes value={noteCategory} onChange={setNoteCategory} />
                        </div>
                        {!noteCategory && (
                          <p className="text-[11px] text-amber-600">Select Research Mapping, Follow-up, or Office Visit to continue.</p>
                        )}
                        <input
                          type="text"
                          value={noteDetail}
                          onChange={(e) => setNoteDetail(e.target.value)}
                          placeholder="Title"
                          className="h-8 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9] transition-colors"
                        />
                        {noteCategory && (
                          <p className="text-[10px] text-neutral-400">
                            Subject will be saved as: <strong>{buildSubject()}</strong>
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Subject *</label>
                        <input
                          type="text"
                          value={logSubject}
                          onChange={(e) => setLogSubject(e.target.value)}
                          placeholder="e.g. Proposal follow-up"
                          className="h-8 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9] transition-colors"
                        />
                      </div>
                    )}

                    {logType === "CALL" && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Primary Phone</label>
                            <input type="tel" value={primaryPhone} onChange={(e) => setPrimaryPhone(e.target.value)}
                              placeholder="+254 7XX XXX XXX"
                              className="h-8 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9] w-full" />
                          </div>
                          <div>
                            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Secondary Phone</label>
                            <input type="tel" value={secondaryPhone} onChange={(e) => setSecondaryPhone(e.target.value)}
                              placeholder="+254 7XX XXX XXX"
                              className="h-8 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9] w-full" />
                          </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Record As</label>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setCallMode("notes")}
                              className="py-2 rounded-lg border text-[12px] font-medium transition-all"
                              style={
                                callMode === "notes"
                                  ? { background: "#fef3c7", color: "#d97706", borderColor: "#d9770660" }
                                  : { background: "#fff", color: "#525252", borderColor: "#e5e5e5" }
                              }
                            >
                              Notes
                            </button>
                            <button
                              type="button"
                              onClick={() => setCallMode("audio")}
                              className="py-2 rounded-lg border text-[12px] font-medium transition-all"
                              style={
                                callMode === "audio"
                                  ? { background: "#dcfce7", color: "#16a34a", borderColor: "#16a34a60" }
                                  : { background: "#fff", color: "#525252", borderColor: "#e5e5e5" }
                              }
                            >
                              Audio Recording
                            </button>
                          </div>
                        </div>

                        {callMode === "audio" && (
                          <AudioRecorder
                            key={formKey}
                            disabled={formLoading}
                            onRecordingComplete={(blob, url, duration) => {
                              setAudioBlob(blob);
                              setAudioDurationSeconds(duration);
                            }}
                            onClear={() => {
                              setAudioBlob(null);
                              setAudioDurationSeconds(0);
                            }}
                            onError={(msg) => toast.error(msg)}
                          />
                        )}
                      </>
                    )}

                    {logType === "EMAIL" && (
                      <>
                        <div>
                          {!selectedContact ? (
                            <p className="text-[11px] text-neutral-400">
                              Select a company first to choose recipients.
                            </p>
                          ) : loadingPersons ? (
                            <p className="text-[11px] text-neutral-400">Loading contacts…</p>
                          ) : (
                            <>
                              <EmailRecipientsField
                                options={emailRecipientOptions}
                                to={primaryEmail}
                                cc={ccEmails}
                                bcc={bccEmails}
                                onToChange={setPrimaryEmail}
                                onCcChange={setCcEmails}
                                onBccChange={canUseBcc ? setBccEmails : undefined}
                                showBcc={canUseBcc}
                                disabled={formLoading}
                              />
                              {emailRecipientOptions.length === 0 && (
                                <p className="text-[10px] text-amber-600 mt-1">
                                  No registered email found for this company yet.
                                </p>
                              )}
                            </>
                          )}
                        </div>

                        <div>
                          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Attachment (PDF only)</label>
                          {!attachment ? (
                            <label className="flex items-center justify-center gap-2 h-8 border border-dashed border-neutral-300 rounded-lg text-[12px] text-neutral-500 cursor-pointer hover:border-[#009DB9] hover:text-[#009DB9] transition-colors">
                              <Paperclip size={12} /> Attach PDF
                              <input type="file" accept="application/pdf" className="hidden" onChange={handleAttachmentChange} />
                            </label>
                          ) : (
                            <div className="flex items-center justify-between h-8 px-3 border rounded-lg border-neutral-200 bg-neutral-50">
                              <span className="text-[12px] text-neutral-700 truncate">{attachment.name}</span>
                              <button type="button" onClick={() => setAttachment(null)} className="flex-shrink-0 text-neutral-400 hover:text-red-500">
                                <X size={12} />
                              </button>
                            </div>
                          )}
                          {attachmentError && <p className="text-[10px] text-red-500 mt-0.5">{attachmentError}</p>}
                        </div>

                        <div>
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Email Body *</label>
                            {emailBodyHtml && (
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => setShowEmailBodyModal(true)}
                                  className="flex items-center gap-1 text-[11px] font-semibold text-[#009DB9] hover:underline"
                                >
                                  <Edit2 size={11} /> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEmailBodyHtml("")}
                                  className="flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:underline"
                                >
                                  <Trash2 size={11} /> Delete
                                </button>
                              </div>
                            )}
                          </div>

                          {!emailBodyHtml ? (
                            <button
                              type="button"
                              onClick={() => setShowEmailBodyModal(true)}
                              className="flex items-center justify-center gap-2 h-8 w-full border border-dashed border-neutral-300 rounded-lg text-[12px] text-neutral-500 hover:border-[#009DB9] hover:text-[#009DB9] transition-colors"
                            >
                              <Plus size={12} /> Write Email Body
                            </button>
                          ) : (
                            <div
                              className="border border-neutral-200 rounded-lg px-3 py-2 text-[12px] text-neutral-800 bg-neutral-50 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                              dangerouslySetInnerHTML={{ __html: emailBodyHtml }}
                            />
                          )}
                        </div>
                      </>
                    )}

                    {logType === "MEETING" && (
                      <div>
                        <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Meeting Link</label>
                        <input type="url" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)}
                          placeholder="https://meet.google.com/..."
                          className="w-full h-8 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9]" />
                      </div>
                    )}

                    {logType !== "NOTE" && logType !== "EMAIL" && (
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Duration (minutes)</label>
                        <input type="number" value={logDuration} onChange={(e) => setLogDuration(e.target.value)}
                          placeholder="30"
                          className="h-8 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9]" />
                      </div>
                    )}

                    {showGenericNotesField && (
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
                          {logType === "NOTE" ? "Note Body" : "Notes"}
                        </label>
                        <textarea rows={logType === "NOTE" ? 5 : 3} value={logNotes} onChange={(e) => setLogNotes(e.target.value)}
                          placeholder={logType === "NOTE" ? "Write your note here..." : "What happened?"}
                          className="border border-neutral-200 rounded-lg px-3 py-2 text-[12px] outline-none focus:border-[#009DB9] resize-none" />
                      </div>
                    )}

                    <button
                      onClick={handleSave}
                      disabled={!logContactId || !isSubjectValid || !isEmailReady || formLoading}
                      className="w-full py-2.5 rounded-xl text-white text-[13px] font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                      style={{ background: "#009DB9" }}
                      onMouseEnter={(e) => { if (logContactId && isSubjectValid && isEmailReady && !formLoading) e.currentTarget.style.background = "#007a90"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}
                    >
                      {formLoading ? (
                        <Loader size={14} className="animate-spin" />
                      ) : logType === "EMAIL" ? (
                        <Send size={13} />
                      ) : null}
                      {formLoading ? (logType === "EMAIL" ? "Sending..." : "Saving...") : logType === "EMAIL" ? "Send Email" : "Save Interaction"}
                    </button>

                    <p className="text-[11px] text-neutral-400 text-center -mt-1">Form resets after saving.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}